import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a <theme-drawer>.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by <theme-drawer>.
 * The cart:view event is auto-dispatched by CartItemsComponent via the
 * view-event-trigger="dialog" attribute.
 *
 * This component handles:
 * - auto-open on add-to-cart
 * - sticky summary layout
 * - installments CTA close-on-click
 * - reliable bundle-progress refreshes after cart changes / drawer opens
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
      this.closest('theme-drawer')
    );
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    // The restore path can set [open] before this module loads,
    // so the drawer-open event may already have fired.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );
  }

  /**
   * Ask the bundle progress controller to reload the authoritative /cart.js state.
   * Falls back to a DOM event if the progress snippet has not initialized yet.
   *
   * @param {string} reason
   * @param {number} delay
   */
  #refreshBundleProgress = (reason = 'cart-drawer', delay = 50) => {
    const controller = window.HKCartProgress;

    if (controller && typeof controller.refresh === 'function') {
      controller.refresh({
        reason,
        delay,
      });

      return;
    }

    document.dispatchEvent(
      new CustomEvent('hk:cart-progress-refresh', {
        detail: {
          reason,
          delay,
        },
      })
    );
  };

  /**
   * Handles the theme-drawer opening.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    /**
     * Immediately paint the latest cached cart state.
     * This prevents the progress bar from briefly returning
     * to its default state after Shopify replaces drawer HTML.
     */
    const controller = window.HKCartProgress;

    if (controller && typeof controller.hydrate === 'function') {
      controller.hydrate();
    }

    /**
     * Then reload /cart.js so the progress bar always matches
     * the real current Shopify cart.
     */
    requestAnimationFrame(() => {
      this.#refreshBundleProgress('drawer-open', 0);
    });

    /**
     * Close cart drawer when installments CTA is clicked
     * to avoid overlapping dialogs.
     *
     * Re-query every time because Shopify can replace
     * the payment-terms shadow root during cart renders.
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector(
        'shopify-payment-terms'
      )?.shadowRoot?.querySelector('#shopify-installments-cta');

      cta?.addEventListener(
        'click',
        () => this.#themeDrawer?.close(),
        { once: true }
      );
    });
  };

  /**
   * Handles cart line updates.
   *
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen =
      this.hasAttribute('auto-open') &&
      event.action === 'add' &&
      !this.#themeDrawer?.isOpen;

    /**
     * If add-to-cart came from a modal such as Quick Add,
     * wait until that modal closes before opening the cart drawer.
     */
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element
        ? event.target.closest('dialog:modal')
        : null
    );

    /**
     * Open immediately when safe.
     */
    if (
      shouldAutoOpen &&
      !sourceModal &&
      !this.#isCartEmpty()
    ) {
      this.#themeDrawer?.open();
    }

    /**
     * Trigger an early refresh.
     *
     * The progress controller debounces this and then
     * performs an authoritative /cart.js refresh.
     */
    this.#refreshBundleProgress(
      `cart-lines-${event.action || 'update'}-start`,
      80
    );

    event.promise
      ?.then(({ detail }) => {
        /**
         * Wait until Shopify has finished updating/replacing
         * the cart drawer HTML.
         */
        const settle = () =>
          requestAnimationFrame(() => {
            this.#updateStickyState();

            /**
             * Refresh again AFTER the cart drawer DOM settles.
             *
             * This is important because Shopify can replace the
             * whole progress-bar element after the first update.
             */
            this.#refreshBundleProgress(
              `cart-lines-${event.action || 'update'}-settled`,
              30
            );
          });

        /**
         * No automatic drawer opening required,
         * or Shopify reported an error.
         */
        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) {
            this.#themeDrawer?.open();
          }

          settle();
        };

        /**
         * If Quick Add is still open,
         * wait for it to close first.
         */
        if (sourceModal?.open) {
          sourceModal.addEventListener(
            'close',
            openAndSettle,
            { once: true }
          );
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        /**
         * Even if a cart update promise fails,
         * reload the real Shopify cart state.
         */
        this.#refreshBundleProgress(
          'cart-lines-error-resync',
          100
        );

        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-drawer] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * Check whether the drawer is showing the empty-cart state.
   */
  #isCartEmpty() {
    return Boolean(
      this.querySelector('.cart-drawer--empty')
    );
  }

  /**
   * Decide whether the summary can remain sticky.
   */
  #updateStickyState() {
    const dialog = this.#dialog;

    if (!dialog) return;

    /**
     * Refs do not cross nested component boundaries,
     * so query inside the dialog directly.
     */
    const content = dialog.querySelector(
      '.cart-drawer__content'
    );

    const summary = dialog.querySelector(
      '.cart-drawer__summary'
    );

    /**
     * Avoid leaving the drawer stuck in sticky mode
     * when the cart becomes empty.
     */
    if (!content || !summary) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }

    const drawerHeight =
      dialog.getBoundingClientRect().height;

    const summaryHeight =
      summary.getBoundingClientRect().height;

    /**
     * Avoid division by zero while the drawer
     * is temporarily hidden or being rendered.
     */
    if (!drawerHeight) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }

    const ratio =
      summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold
        ? 'false'
        : 'true'
    );
  }
}

/**
 * Prevent duplicate registration if Shopify
 * reloads the module inside the Theme Editor.
 */
if (!customElements.get('cart-drawer-component')) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}